import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { HiViewGrid } from "react-icons/hi";
import { FaArrowAltCircleLeft, FaRegArrowAltCircleRight } from "react-icons/fa";
import { auth, db } from "../pages/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import Avatar from "react-avatar";
import { useNavigate } from "react-router-dom";

import { LuUserCog } from "react-icons/lu";
import { AiOutlineLogout } from "react-icons/ai";
import { BiHelpCircle } from "react-icons/bi";
import { FaSquarespace } from "react-icons/fa";
import { TbHomeMove } from "react-icons/tb";
import { RiUser2Fill } from "react-icons/ri";

import { PiMicrosoftExcelLogoFill } from "react-icons/pi";

export const Sidebar = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [trialEndTime, setTrialEndTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "user", currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser(userData);

          // Check subscription or trial status
          const subscriptionEndDate = userData.subscriptionEndDate;
          const firestoreTrialEndTime = userData.trialEndTime?.toDate(); // Assuming it's a Firestore Timestamp

          if (subscriptionEndDate) {
            const currentDate = new Date();
            const endDate = new Date(subscriptionEndDate.toDate());
            if (currentDate > endDate) {
              setSubscriptionExpired(true);
              setShowPopup(true);
            }
          } else if (firestoreTrialEndTime) {
            setTrialEndTime(firestoreTrialEndTime);
          } else {
            // If no subscription or trial, initialize trial end time
            const currentDate = new Date();
            const trialEnd = new Date(
              currentDate.getTime() + 30 * 24 * 60 * 60 * 1000
            );
            setTrialEndTime(trialEnd);

            // Save trial end time to Firestore
            await setDoc(
              userDocRef,
              { trialEndTime: trialEnd },
              { merge: true }
            );
          }
        } else {
          console.error("No such document in Firestore!");
        }
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      if (trialEndTime) {
        const currentTime = new Date().getTime();
        const endTime = new Date(trialEndTime).getTime();
        const timeRemaining = endTime - currentTime;

        if (timeRemaining <= 0) {
          clearInterval(timerInterval);
          setTimeLeft("Trial expired");
          setSubscriptionExpired(true); // Ensure the state is updated
          setShowPopup(true);
        } else {
          const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
          const hours = Math.floor(
            (timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
          );
          const minutes = Math.floor(
            (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
          );
          const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
          setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
        }
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [trialEndTime]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev); // Use functional state update
  };

  const handleLogout = () => {
    const confirmLogout = window.confirm("Are you sure you want to log out?");
    if (confirmLogout) {
      auth
        .signOut()
        .then(() => {
          console.log("User signed out");
          localStorage.removeItem("authToken");
          localStorage.removeItem("popup");

          navigate("/");
        })
        .catch((error) => {
          console.error("Error signing out: ", error);
        });
    }
  };

  const isActive = (path) => (location.pathname === path ? "bg-gray-700" : "");

  return (
    <>
      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full transition-transform ease-in-out duration-300 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-16"
        } bg-gray-800 text-white shadow-md z-50`}
        style={{
          minWidth: isSidebarOpen ? "200px" : "64px", // Minimum width for expanded state
          maxWidth: isSidebarOpen ? "250px" : "64px", // Maximum width for expanded state
        }}
      >
        <div className="flex flex-col h-full p-6">
          {/* User Profile */}
          {/* User Profile */}
          <div
            className={`flex items-center mb-5 ${
              isSidebarOpen ? "" : "justify-center"
            }`}
            style={{
              flexWrap: "nowrap",
              alignItems: "center",
              overflow: "hidden",
            }} // Ensure no wrapping and content stays within bounds
          >
            <Link to="/acc_Profile" className="flex-shrink-0">
              {" "}
              {/* Prevent the image from shrinking */}
              {user?.photo ? (
                <img
                  src={user.photo}
                  alt="Profile"
                  className={`h-11 w-11 rounded-full ${
                    isSidebarOpen ? "mr-2" : ""
                  }`}
                />
              ) : (
                <Avatar
                  name={user?.firstName + " " + user?.lastName || "User"} // Fallback to user's name
                  size="35" // Adjust the size as needed
                  round
                  className={`h-9 w-9 ${isSidebarOpen ? "mr-2" : ""}`}
                />
              )}
            </Link>
            {isSidebarOpen && (
              <div className=" flex flex-col flex-grow">
                <span
                  className="text-sm font-medium truncate"
                  style={{
                    display: "block", // Ensure the text is a block element for truncation to work
                    minWidth: 0, // Allow the text to overflow within bounds
                    overflow: "hidden",
                    textOverflow: "ellipsis", // Truncate the text if it's too long
                    whiteSpace: "nowrap", // Prevent wrapping of the name
                  }}
                >
                  {user ? `${user.firstName} ${user.lastName}` : " "}
                </span>
                <span className="text-xs text-yellow-500 block">
                  {subscriptionExpired
                    ? "Subscribe"
                    : user?.plan || "Free Trial"}
                </span>
              </div>
            )}
          </div>

          {/* Subscription and Trial Status */}
          {!subscriptionExpired &&
            timeLeft !== "Trial Expired" &&
            trialEndTime &&
            timeLeft && (
              <div className="text-green-500 text-center mb-4">
                <p className="text-xs truncate">
                  Free trial ends in: {timeLeft}
                </p>
              </div>
            )}
          {/* {subscriptionExpired && (
  <div className="text-red-500 text-center mb-4">
    <p>Your subscription has expired. Please subscribe to continue.</p>
  </div>
)}
{timeLeft === "Trial Expired" && !subscriptionExpired && (
  <div className="text-red-500 text-center mb-4">
    <p>Your free trial has ended. Please subscribe to continue.</p>
  </div>
)} */}

          {/* Links */}
          <div className="flex flex-col space-y-2 mb-4">
            {[
              { path: "/", label: "Home", Icon: TbHomeMove },
              { path: "/acc_profile", label: "Profile", Icon: LuUserCog },
              {
                path: "/help_guide",
                label: "User Guide",
                Icon: RiUser2Fill,
                isExternal: true,
              },
              {
                path:
                  subscriptionExpired || timeLeft === "Trial Expired"
                    ? "#"
                    : "/Gst_Form",
                label: "GST Reconciliation",
                Icon: FaSquarespace,
                disabled: subscriptionExpired || timeLeft === "Trial Expired",
              },

              {
                path:
                  subscriptionExpired || timeLeft === "Trial Expired"
                    ? "#"
                    : "/Ledger_Conversion",
                label: "Ledger pro",
                Icon: PiMicrosoftExcelLogoFill,
                disabled: subscriptionExpired || timeLeft === "Trial Expired",
              },
              {
                path: "/pricing",
                label: "Pricing",
                Icon: RiMoneyDollarCircleLine,
              },
              {
                path: "/acc_how_to_use",
                label: "How to use",
                Icon: HiViewGrid,
              },
              { path: "/HelpPage", label: "Help", Icon: BiHelpCircle },
            ].map(({ path, label, Icon, disabled, isExternal }) =>
              isExternal ? (
                <a
                  key={path}
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center p-2 rounded-md transition-colors hover:bg-gray-700`}
                >
                  <Icon className="text-xl" />
                  {isSidebarOpen && <span className="ml-2">{label}</span>}
                </a>
              ) : (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center p-2 rounded-md transition-colors hover:bg-gray-700 ${isActive(
                    path
                  )} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={(e) => {
                    if (disabled) {
                      e.preventDefault();
                      alert(
                        "Your subscription has expired. Please renew to access this feature."
                      );
                    }
                  }}
                >
                  <Icon className="text-xl" />
                  {isSidebarOpen && <span className="ml-2">{label}</span>}
                </Link>
              )
            )}
          </div>

          {/* Logout Button */}
          <div className="flex flex-col space-y-2">
            <button
              onClick={handleLogout}
              className="flex items-center w-full p-2 rounded-md transition-colors text-red-600 hover:bg-gray-700"
            >
              <AiOutlineLogout className="text-xl" />
              {isSidebarOpen && <span className="ml-2">Logout</span>}
            </button>
          </div>

          {/* Sidebar Toggle Button */}
          <div
            className={`absolute top-1/2 -translate-y-1/2 right-0 flex items-center justify-center ${
              isSidebarOpen
                ? "bg-white text-gray-800"
                : "bg-gray-800 text-white"
            } rounded-l-full w-8 h-8`}
          >
            <button
              onClick={toggleSidebar}
              className="w-full h-full flex items-center justify-center"
            >
              <FaRegArrowAltCircleRight
                className={`transform ${
                  isSidebarOpen ? "rotate-180" : ""
                } transition-transform`}
                size={20}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar icon to open */}
      {!isSidebarOpen && (
        <div className="fixed top-1/2 -translate-y-1/2 left-0 flex items-center justify-center bg-gray-800 text-white rounded-r-full w-8 h-8 z-50">
          <button
            onClick={toggleSidebar}
            className="w-full h-full flex items-center justify-center"
          >
            <FaArrowAltCircleLeft
              className="transform rotate-180 transition-transform"
              size={20}
            />
          </button>
        </div>
      )}
      {location.pathname === "/Gst_Form" && showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-black rounded-lg shadow-lg p-6 w-96 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">
                Subscription Required
              </h2>
              {/* <button
          onClick={() => setShowPopup(false)}
          className="text-gray-400 hover:text-gray-200 transition"
        >
          ✕
        </button> */}
            </div>
            <p className="text-sm text-gray-300 mb-4">
              Your subscription has expired or your trial has ended. Please
              subscribe to continue using our services.
            </p>
            <div className="flex justify-end">
              <Link
                to="/pricing"
                className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700"
              >
                Subscribe Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
